import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useTheme, styled } from '@mui/material/styles';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import ButtonBase from '@mui/material/ButtonBase';
import InputBase from '@mui/material/InputBase';
import Box from '@mui/material/Box';

const StyledAutocompletePopper = styled('div')(({ theme }) => ({
    [`& .${autocompleteClasses.paper}`]: {
        boxShadow: 'none',
        margin: 0,
        color: 'inherit',
        fontSize: 13,
    },
    [`& .${autocompleteClasses.listbox}`]: {
        backgroundColor: '#fff',
        padding: 0,
        [`& .${autocompleteClasses.option}`]: {
            minHeight: 'auto',
            alignItems: 'flex-start',
            padding: 8,
            borderBottom: `1px solid  ${' #eaecef'}`,
            '&[aria-selected="true"]': {
                backgroundColor: 'transparent',
            },
            [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
            {
                backgroundColor: theme.palette.action.hover,
            },
            ...theme.applyStyles('dark', {
                borderBottom: `1px solid  ${'#666666'}`,
            }),
        },
        ...theme.applyStyles('dark', {
            backgroundColor: '#1c2128',
        }),
    },
    [`&.${autocompleteClasses.popperDisablePortal}`]: {
        position: 'relative',
    },
}));

function PopperComponent(props) {
    const { disablePortal, anchorEl, open, ...other } = props;
    return <StyledAutocompletePopper {...other} />;
}

PopperComponent.propTypes = {
    anchorEl: PropTypes.any,
    disablePortal: PropTypes.bool,
    open: PropTypes.bool.isRequired,
};

const StyledPopper = styled(Popper)(({ theme }) => ({
    border: `1px solid ${'#e1e4e8'}`,
    boxShadow: `0 8px 24px ${'rgba(149, 157, 165, 0.2)'}`,
    color: '#24292e',
    backgroundColor: '#fff',
    borderRadius: 6,
    width: 300,
    zIndex: theme.zIndex.modal,
    fontSize: 13,
    ...theme.applyStyles('dark', {
        border: `1px solid ${'#30363d'}`,
        boxShadow: `0 8px 24px ${'rgb(1, 4, 9)'}`,
        color: '#c9d1d9',
        backgroundColor: '#1c2128',
    }),
}));

const StyledInput = styled(InputBase)(({ theme }) => ({
    padding: 10,
    width: '100%',
    borderBottom: `1px solid ${'#666666'}`,
    '& input': {
        borderRadius: 4,
        backgroundColor: '#fff',
        border: `1px solid ${'#666666'}`,
        padding: 8,
        transition: theme.transitions.create(['border-color', 'box-shadow']),
        fontSize: 14,
        '&:focus': {
            boxShadow: `0px 0px 0px 3px ${'rgba(3, 102, 214, 0.3)'}`,
            borderColor: '#0366d6',
            ...theme.applyStyles('dark', {
                boxShadow: `0px 0px 0px 3px ${'rgb(12, 45, 107)'}`,
                borderColor: '#666666',
            }),
        },
        ...theme.applyStyles('dark', {
            backgroundColor: '#0d1117',
            border: `1px solid ${'#eaecef'}`,
        }),
    },
    ...theme.applyStyles('dark', {
        borderBottom: `1px solid ${'#eaecef'}`,
    }),
}));

const Button = styled(ButtonBase)(({ theme }) => ({
    fontSize: 13,
    width: '100%',
    textAlign: 'left',
    paddingBottom: 8,
    color: '#586069',
    fontWeight: 600,
    '&:hover,&:focus': {
        color: '#0366d6',
        ...theme.applyStyles('dark', {
            color: '#58a6ff',
        }),
    },
    '& span': {
        width: '100%',
    },
    '& svg': {
        width: 16,
        height: 16,
    },
    ...theme.applyStyles('dark', {
        color: '#8b949e',
    }),
}));

export default function StyledList({ total_episodes, episodes, updateField, selectedEpisodes }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [pendingValue, setPendingValue] = useState(selectedEpisodes);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const [isAllSelected, setIsAllSelected] = useState(false);

    const handleSelectAll = () => {
        if (isAllSelected) {
            setPendingValue([]);
            updateField('exp_assigned_episodes', []);
        } else {
            setPendingValue(total_episodes);
            updateField('exp_assigned_episodes', total_episodes);
        }
        setIsAllSelected(!isAllSelected);
    };

    const handleClose = () => {
        setPendingValue(selectedEpisodes);
        if (anchorEl) {
            anchorEl.focus();
        }
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'episodes-list' : undefined;

    const handleEpisodeChange = (selected) => {
        updateField('exp_assigned_episodes', selected);
    };

    useEffect(() => {
        setPendingValue([]);
        updateField('exp_assigned_episodes', []);
    }, [episodes]);

    useEffect(() => {
        if (selectedEpisodes && selectedEpisodes.length > 0) {
            setPendingValue(selectedEpisodes);
            updateField('exp_assigned_episodes', selectedEpisodes);
        }
    }, [selectedEpisodes]);

    const removeEpisode = (episode) => {
        const updatedEpisodes = pendingValue.filter((e) => e !== episode);
        setPendingValue(updatedEpisodes);
        updateField('exp_assigned_episodes', updatedEpisodes);
    };

    return (
        <Fragment>
            <Box sx={{
                width: '100%',
                fontSize: 14,
                alignItems: 'center',
            }}
            >
                <fieldset
                    style={{
                        border: '1px solid rgba(0, 0, 0, 0.23)',
                        borderRadius: '4px',
                        overflowY: 'scroll',
                        maxHeight: '88%',
                        height: '715px',
                        width: '100%',
                        marginRight: '200px',
                    }}
                >
                    <legend
                        style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#666666',
                        }}
                    >
                        Επεισόδια
                    </legend>
                    <Button
                        disableRipple
                        aria-describedby={id}
                        onClick={handleClick}>
                        <span>Επιλέξτε Επεισόδια</span>
                        <ArrowDropDownIcon />
                    </Button>
                    {pendingValue.map((episode) => (
                        <Box
                            key={episode}
                            sx={{
                                mt: '3px',
                                height: 24,
                                padding: '.15em 4px',
                                lineHeight: '15px',
                                borderRadius: '2px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: (theme) => theme.palette.background.paper,
                                color: (theme) => theme.palette.text.primary,
                            }}
                        >
                            <legend>Επεισόδιο {episode} </legend>
                            <CloseIcon
                                sx={{ ml: 1, cursor: 'pointer', color: (theme) => theme.palette.error.main, }}
                                onClick={() => removeEpisode(episode)}
                            />
                        </Box>
                    ))}
                </fieldset>
            </Box>
            <StyledPopper
                id={id}
                open={open}
                anchorEl={anchorEl}
                placement="bottom-start">
                <ClickAwayListener
                    onClickAway={handleClose}>
                    <div>
                        <Box
                            sx={{
                                borderBottom: `1px solid #666666`,
                                padding: '8px 10px',
                                fontWeight: 600,
                            }}
                        >
                            Επιλέξτε Επεισόδια
                        </Box>
                        <Box>
                            {/* Select All / Unselect All Button */}
                            <Button onClick={handleSelectAll} sx={{ padding: '8px 10px', fontWeight: 600, textAlign: 'center', width: '100%' }}>
                                {isAllSelected ? 'Αποεπιλέξτε όλα' : 'Επιλογή όλων'}
                            </Button>
                            <Autocomplete
                                open
                                multiple
                                value={pendingValue}
                                onChange={(event, newValue) => {
                                    setPendingValue(newValue.sort((a, b) => a - b)); // Sorting episodes on selection
                                    handleEpisodeChange(newValue.sort((a, b) => a - b));
                                }}
                                disableCloseOnSelect
                                renderTags={() => null}
                                noOptionsText="Δεν υπάρχουν διαθέσιμα επεισόδια"
                                options={total_episodes}
                                getOptionLabel={(episode) => `Episode ${episode}`}
                                renderOption={(props, episode, { selected }) => (
                                    <li {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {selected ? (
                                                <DoneIcon color="primary" />
                                            ) : (
                                                <CloseIcon sx={{ opacity: 0 }} />
                                            )}
                                            <span>{`Επεισόδιο ${episode}`}</span>
                                        </Box>
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <StyledInput
                                        ref={params.InputProps.ref}
                                        inputProps={params.inputProps}
                                        autoFocus
                                        placeholder="Αναζήτηση επεισοδίων"
                                    />
                                )}
                            />
                        </Box>
                    </div>
                </ClickAwayListener>
            </StyledPopper>
        </Fragment >
    );
}
